import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Clock } from 'lucide-react';

const footerLinks = {
  services: [
    { name: 'PolicyCenter', href: '/services/policycenter' },
    { name: 'ClaimCenter', href: '/services/claimcenter' },
    { name: 'BillingCenter', href: '/services/billingcenter' },
    { name: 'Cloud & Modernisation', href: '/cloud-modernisation' },
    { name: 'QA & Testing', href: '/qa-testing' },
    { name: 'Staffing & Talent', href: '/staffing' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Resources', href: '/resources' },
    { name: 'Contact', href: '/contact' },
  ],
  resources: [
    { name: 'Whitepapers', href: '/resources#whitepapers' },
    { name: 'Blog', href: '/resources#blog' },
    { name: 'Webinars', href: '/resources#webinars' },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6">
              <img 
                src="/guidepro-logo.png" 
                alt="GuidePro Inc." 
                className="h-14 w-auto object-contain"
                style={{ minWidth: '160px' }}
              />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm">
              Empowering insurance enterprises with AI-powered Guidewire solutions. 
              Your trusted partner for implementation, modernisation, and digital transformation.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors duration-200"
                data-testid="linkedin-link"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-sky-600 flex items-center justify-center transition-colors duration-200"
                data-testid="twitter-link"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-sky-400 text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-sky-400 text-sm transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  110 East Main Street<br />
                  Suite 722, Madison<br />
                  Wisconsin 53703, USA
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-sky-400 flex-shrink-0" />
                <a 
                  href="tel:+16089878000" 
                  className="text-slate-400 hover:text-sky-400 text-sm transition-colors duration-200"
                >
                  (608) 987-8000
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-sky-400 flex-shrink-0" />
                <a 
                  href="mailto:info@guideproinc.com" 
                  className="text-slate-400 hover:text-sky-400 text-sm transition-colors duration-200"
                >
                  info@guideproinc.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  Mon–Fri: 09:00 am – 05:00 pm<br />
                  Sat–Sun: Closed
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Guidepro Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors duration-200">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
